import { cn } from '@/lib/utils'
import { collaborationStatusConfig, type CollaborationStatusKey } from '@/lib/section-status'
import type { FullResponseSection as Section } from '@/lib/api'

interface FullResponseOutlineProps {
  sections: Section[]
  statusByKey: Map<string, CollaborationStatusKey>
  activeKey: string | null
  onSelect: (sectionKey: string) => void
}

/**
 * Reading outline: number, title, status dot. No editing affordances. Width
 * and the seam on its right come from the page's resizable pane + handle.
 */
export function FullResponseOutline({ sections, statusByKey, activeKey, onSelect }: FullResponseOutlineProps) {
  return (
    <nav aria-label="Response outline" className="h-full overflow-y-auto bg-frame py-3">
      <div className="px-3.5 pb-2 text-meta font-medium uppercase tracking-[.08em] text-subtle">Response</div>
      <ul className="space-y-0.5 px-2">
        {sections.map((s, i) => {
          const status = statusByKey.get(s.sectionKey) ?? 'not_started'
          const dot = collaborationStatusConfig[status].dotColor
          const isActive = s.sectionKey === activeKey
          return (
            <li key={s.sectionKey}>
              <button
                type="button"
                aria-current={isActive ? 'location' : undefined}
                aria-label={`${i + 1}. ${s.title}`}
                onClick={() => onSelect(s.sectionKey)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-[6px] border-l-2 border-transparent py-1.5 pl-2 pr-2 text-left text-sm text-sidebar-foreground hover:bg-foreground/[0.05]',
                  isActive && 'border-l-[hsl(var(--brand))] bg-foreground/10 font-medium text-foreground',
                )}
              >
                <span className="tabular-nums text-muted-foreground">{i + 1}.</span>
                <span className="min-w-0 flex-1 truncate">{s.title}</span>
                <span className={cn('h-2 w-2 shrink-0 rounded-full', dot)} aria-hidden="true" />
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
